import { format, isSameDay, parseISO } from 'date-fns';

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function normalizeReservationStatus(status) {
  const normalized = String(status || 'pending')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');

  switch (normalized) {
    case 'confirmed':
    case 'pending':
    case 'seated':
    case 'completed':
    case 'cancelled':
    case 'no_show':
      return normalized;
    default:
      return normalized || 'pending';
  }
}

export function parseReservationDate(reservation) {
  const raw = String(reservation?.reservation_date || '').trim();
  if (!raw) return null;

  try {
    if (DATE_ONLY_PATTERN.test(raw)) {
      return parseISO(raw);
    }
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function getReservationTimeValue(reservation) {
  const rawTime = String(reservation?.reservation_time || '').trim();
  if (rawTime) {
    return rawTime.slice(0, 5);
  }

  const date = parseReservationDate(reservation);
  return date ? format(date, 'HH:mm') : '';
}

export function getReservationDateTime(reservation) {
  const baseDate = parseReservationDate(reservation);
  if (!baseDate) return null;

  const rawTime = String(reservation?.reservation_time || '').trim();
  if (!rawTime) return baseDate;

  const [hours, minutes] = rawTime.split(':').map((value) => Number(value || 0));
  const next = new Date(baseDate);
  next.setHours(hours || 0, minutes || 0, 0, 0);
  return next;
}

export function isReservationOnDay(reservation, date) {
  const reservationDate = parseReservationDate(reservation);
  if (!reservationDate || !date) return false;
  return isSameDay(reservationDate, date);
}

export function formatReservationDateLabel(reservation) {
  const date = parseReservationDate(reservation);
  if (!date) return '—';
  return format(date, 'EEE, dd. MMM');
}

export function formatReservationMonthDay(reservation) {
  const date = parseReservationDate(reservation);
  if (!date) {
    return { month: '---', day: '--' };
  }

  return {
    month: format(date, 'MMM'),
    day: format(date, 'd'),
  };
}

export function getStatusMeta(status) {
  const normalized = normalizeReservationStatus(status);
  switch (normalized) {
    case 'confirmed':
      return {
        value: normalized,
        label: 'Bestaetigt',
        badgeClass: 'bg-blue-50 text-blue-700 border-blue-200',
        pillClass: 'bg-blue-100 text-blue-800 border-blue-200',
      };
    case 'seated':
      return {
        value: normalized,
        label: 'Anwesend',
        badgeClass: 'bg-green-50 text-green-700 border-green-200',
        pillClass: 'bg-green-100 text-green-800 border-green-200',
      };
    case 'completed':
      return {
        value: normalized,
        label: 'Abgeschlossen',
        badgeClass: 'bg-slate-50 text-slate-700 border-slate-200',
        pillClass: 'bg-slate-100 text-slate-800 border-slate-200',
      };
    case 'cancelled':
      return {
        value: normalized,
        label: 'Storniert',
        badgeClass: 'bg-rose-50 text-rose-700 border-rose-200',
        pillClass: 'bg-rose-100 text-rose-800 border-rose-200',
      };
    case 'no_show':
      return {
        value: normalized,
        label: 'No-Show',
        badgeClass: 'bg-orange-50 text-orange-700 border-orange-200',
        pillClass: 'bg-orange-100 text-orange-800 border-orange-200',
      };
    case 'pending':
    default:
      return {
        value: 'pending',
        label: 'Offen',
        badgeClass: 'bg-amber-50 text-amber-700 border-amber-200',
        pillClass: 'bg-amber-100 text-amber-800 border-amber-200',
      };
  }
}

export function getOriginMeta(origin) {
  const normalized = String(origin || 'online').trim().toLowerCase();

  switch (normalized) {
    case 'staff':
      return { label: 'Staff', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    case 'walk-in':
    case 'walk_in':
      return { label: 'Walk-In', className: 'bg-slate-50 text-slate-700 border-slate-200' };
    case 'phone':
      return { label: 'Telefon', className: 'bg-violet-50 text-violet-700 border-violet-200' };
    case 'online':
    default:
      return { label: 'Online', className: 'bg-blue-50 text-blue-700 border-blue-200' };
  }
}

export function extractDiscountInfo(reservation) {
  let percent = reservation?.discount_percentage;
  let isApplied = reservation?.discount_applied;
  let source = 'database';

  if (!percent && (reservation?.special_requests || reservation?.notes)) {
    const text = `${reservation?.special_requests || ''} ${reservation?.notes || ''}`;
    const match = text.match(/\[Discount:\s*(\d+)%?\]/i);
    if (match) {
      percent = Number.parseInt(match[1], 10);
      isApplied = true;
      source = 'notes';
    }
  }

  const normalizedPercent = Number.parseInt(percent || 0, 10) || 0;

  return {
    hasDiscount: isApplied === true || normalizedPercent > 0,
    percentage: normalizedPercent,
    source,
  };
}

export function getReservationGuestName(reservation) {
  return reservation?.guest_name || reservation?.customer_name || reservation?.name || 'Gast';
}

export function getReservationContactPhone(reservation) {
  return reservation?.phone || reservation?.customer_phone || '';
}

export function buildCsvRows(reservations) {
  const header = ['Datum', 'Zeit', 'Gast', 'Personen', 'Status', 'Tisch', 'Telefon', 'Notiz'];
  const rows = reservations.map((reservation) => [
    String(reservation?.reservation_date || ''),
    getReservationTimeValue(reservation),
    getReservationGuestName(reservation),
    String(reservation?.party_size || 0),
    getStatusMeta(reservation?.status).label,
    String(reservation?.table_name || reservation?.table?.name || ''),
    getReservationContactPhone(reservation),
    String(reservation?.notes || reservation?.special_requests || ''),
  ]);

  return [header, ...rows];
}
